import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { Loader2 } from 'lucide-react'
import api from '@/lib/api/axios'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface TimelinePoint {
    objectID: string
    objectName?: string
    datetime: string
    anomaly_score: number
    details?: string
    vaultName?: string
}

const RANGE_OPTIONS = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
] as const

function scoreColor(score: number): string {
    if (score === 0) return '#22c55e'
    if (score <= 3) return '#f59e0b'
    if (score <= 6) return '#f97316'
    return '#ef4444'
}

export function TimelineTab() {
    const { t } = useTranslation()
    const [days, setDays] = useState(30)

    const { data: points = [], isLoading } = useQuery({
        queryKey: ['analytics', 'timeline', days],
        queryFn: async () => {
            const res = await api.get('/api/analytics/timeline', { params: { days } })
            return (res.data?.data || res.data || []) as TimelinePoint[]
        },
    })

    const chartOption = useMemo((): EChartsOption => {
        if (points.length === 0) return {}

        const scatterData = points.map((p) => ({
            value: [p.datetime, p.anomaly_score],
            itemStyle: { color: scoreColor(p.anomaly_score) },
            _raw: p,
        }))

        return {
            tooltip: {
                trigger: 'item',
                formatter: (params: any) => {
                    const raw = params.data?._raw as TimelinePoint | undefined
                    if (!raw) return ''
                    const dt = new Date(raw.datetime).toLocaleString()
                    let html = `<div style="font-weight:600">${dt}</div>`
                    html += `<div>Object: <b>${raw.objectID}</b></div>`
                    html += `<div>Score: <b>${raw.anomaly_score}</b></div>`
                    if (raw.details) html += `<div style="color:#888;font-size:11px">${raw.details}</div>`
                    if (raw.vaultName) html += `<div style="color:#888;font-size:11px">Vault: ${raw.vaultName}</div>`
                    return html
                },
            },
            grid: { left: 50, right: 20, top: 20, bottom: 40 },
            xAxis: {
                type: 'time',
                axisLabel: { fontSize: 10, color: '#888' },
                axisLine: { show: false },
                axisTick: { show: false },
            },
            yAxis: {
                type: 'value',
                name: 'Anomaly Score',
                min: 0,
                max: 8,
                nameTextStyle: { fontSize: 10, color: '#888' },
                axisLabel: { fontSize: 10, color: '#888' },
                splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } },
            },
            series: [
                {
                    type: 'scatter',
                    data: scatterData,
                    symbolSize: 10,
                },
            ],
        }
    }, [points])

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{t('analytics.timeline', 'Anomaly Timeline')}</CardTitle>
                <div className="flex items-center gap-1">
                    {RANGE_OPTIONS.map((opt) => (
                        <button
                            key={opt.days}
                            type="button"
                            onClick={() => setDays(opt.days)}
                            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                                days === opt.days
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'border-border text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Loading timeline...
                    </div>
                ) : points.length === 0 ? (
                    <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
                        No anomaly data for the selected period.
                    </div>
                ) : (
                    <ReactECharts option={chartOption} style={{ height: 400 }} opts={{ renderer: 'canvas' }} />
                )}
            </CardContent>
        </Card>
    )
}
